import { Component, Event, EventEmitter, h, Prop, State, Element } from '@stencil/core';
import { ButtonSize, ButtonType } from '../../../static/buttons/button.interface';
import { IconUIType } from '../../../static/icon/icon-ui-type';
import { ChatbotOptions } from '../chatbot.interface';

@Component({
    tag: 'web-chatbot-input',
    styleUrls: ['chatbot-input.scss'],
    shadow: false
})
export class ChatbotInput implements ChatbotOptions {
    @Prop() placeholderText = '';
    @Prop() submitButtonText = '';
    @Prop() maxLengthText = '';
    /**
     * Maximum length for the input value.
     *
     * This defines the maximum number of characters the user can enter into the input field.
     * The default value is set to 150 characters.
     *
     */
    @Prop() maxlength = 150;
    @Prop() submitButtonEnabled = false;

    @State() inputValue = '';
    /**
     * Event emitted when the input value is submitted.
     *
     * This event is fired when the user submits the input value. The event emits the current
     * value of the input field as a string.
     *
     */
    @Event() valueEmitted: EventEmitter<string>;

    @Element() el: HTMLWebChatbotInputElement;

    private handleKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
            this.onSubmit();
        }
    };

    connectedCallback = () => {
        this.el.addEventListener('keydown', this.handleKeydown);
    };

    disconnectedCallback() {
        this.el.removeEventListener('keydown', this.handleKeydown);
    }

    private handleInputChange = (event: Event) => {
        this.inputValue = (event.target as HTMLInputElement).value;
    };

    private getCharacterIndication = (): string => {
        const remainingChars = this.maxlength - this.inputValue.length;
        const indicator = remainingChars === this.maxlength ? this.maxLengthText : remainingChars;

        return remainingChars <= 10 ? (
            <span class="warning">
                {remainingChars < 0 && '-'}
                {indicator}
            </span>
        ) : (
            `${indicator}`
        );
    };
    private onSubmit = () => {
        if (!this.inputValue.length) return;
        this.valueEmitted.emit(this.inputValue);
        this.inputValue = '';
    };

    render() {
        const { inputValue, placeholderText, submitButtonText, submitButtonEnabled } = this;
        const submitDisabled = inputValue.length < 1 || !submitButtonEnabled;

        return (
            <div class="input">
                <div class="input-container">
                    <input
                        type="text"
                        value={inputValue}
                        onInput={this.handleInputChange}
                        placeholder={placeholderText}
                        aria-label={placeholderText}
                    />
                    <div class="input-max-length">{this.getCharacterIndication()}</div>
                </div>
                <web-button
                    type={ButtonType.primary}
                    size={ButtonSize.large}
                    icon-end={IconUIType.chevron_right}
                    disabled={submitDisabled}
                    onClick={() => this.onSubmit()}
                >
                    <span>{submitButtonText}</span>
                </web-button>
            </div>
        );
    }
}
